import {
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Box,
} from '@material-ui/core'
// import TranslateIcon from '@material-ui/icons/Translate'
import CloseIcon from '@material-ui/icons/Close'
import LanguageIcon from '@material-ui/icons/Language'
import React, {useCallback} from 'react'

import {
  LanguageCode,
  SUPPORTED_LANGUAGES,
  getLanguageName,
} from '../utils/languages' // Adjust path if needed

interface LanguageSelectorProps {
  currentLanguage: LanguageCode
  onLanguageChange: (newLangCode: LanguageCode) => void
  disabled?: boolean // Optional disabled state
  open: boolean // Control dialog visibility externally
  onClose: () => void // Callback to close the dialog
  onOpenRequest: () => void // Callback when user clicks icon to open
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  currentLanguage,
  onLanguageChange,
  disabled = false,
  open,
  onClose,
  onOpenRequest,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleLanguageSelect = useCallback(
    (langCode: LanguageCode) => {
      onLanguageChange(langCode)
      onClose()
    },
    [onLanguageChange, onClose]
  )

  const currentLangName = getLanguageName(currentLanguage)
  const headerHeight =
    typeof theme.mixins.toolbar.minHeight === 'number'
      ? `${theme.mixins.toolbar.minHeight}px`
      : theme.mixins.toolbar.minHeight || '64px'

  const basePaperStyle: React.CSSProperties = {
    boxShadow: theme.shadows[5],
    display: 'flex',
    flexDirection: 'column',
  }

  const mobilePaperStyle: React.CSSProperties = {
    ...basePaperStyle,
    position: 'absolute',
    top: headerHeight,
    left: 0,
    right: 0,
    margin: 0,
    width: '100%',
    height: `calc(100vh - ${headerHeight})`,
    maxHeight: `calc(100vh - ${headerHeight})`,
    maxWidth: '100%',
    borderTop: `1px solid ${theme.palette.divider}`,
    borderRadius: 0,
  }

  const desktopPaperStyle: React.CSSProperties = {
    ...basePaperStyle,
    position: 'relative',
    margin: 'auto',
    marginTop: headerHeight,
    width: '90vw',
    maxWidth: '450px',
    height: 'auto',
    maxHeight: `calc(100% - ${headerHeight} - 32px)`,
    borderRadius: theme.shape.borderRadius,
  }

  return (
    <>
      <Tooltip title={`Current Language: ${currentLangName}`}>
        <span>
          {' '}
          <IconButton
            aria-label="select language"
            onClick={onOpenRequest}
            color="inherit"
            disabled={disabled}
            style={{paddingLeft: 0}}
          >
            <div
              style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <LanguageIcon />
              {/* <TranslateIcon
                style={{
                  position: 'relative',
                  bottom: '-8px',
                  left: '-2px',
                  fontSize: '1rem',
                }}
              /> */}
            </div>
          </IconButton>
        </span>
      </Tooltip>
      <Dialog
        fullScreen={isMobile}
        open={open}
        onClose={onClose}
        aria-labelledby="language-select-dialog-title"
        PaperProps={{
          style: isMobile ? mobilePaperStyle : desktopPaperStyle,
        }}
      >
        <DialogTitle
          id="language-select-dialog-title"
          disableTypography
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(1, 2),
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          {!isMobile && (
            <Box visibility="hidden">
              <IconButton>
                <CloseIcon />
              </IconButton>
            </Box>
          )}
          <Box>
            <LanguageIcon />
            {/* <TranslateIcon
              style={{
                position: 'relative',
                bottom: '-8px',
                left: '-2px',
                fontSize: '1rem',
              }}
            /> */}
          </Box>
          <IconButton
            edge={isMobile ? 'start' : 'end'}
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box style={{flexGrow: 1, overflowY: 'auto'}}>
          <List component="nav" aria-label="language options">
            {SUPPORTED_LANGUAGES.map((language) => (
              <ListItem
                button
                key={language.code}
                selected={language.code === currentLanguage}
                onClick={() => handleLanguageSelect(language.code)}
                style={{textAlign: 'center'}}
              >
                <ListItemText primary={language.name} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Dialog>
    </>
  )
}

export default React.memo(LanguageSelector)
